import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, X, Mail, Phone, Rocket, Search, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Eyebrow } from "../components/Bits";

export default function AdminPilots() {
  const { user, ready } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [confirmWl, setConfirmWl] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [declineCands, setDeclineCands] = useState([]);
  const [declineSel, setDeclineSel] = useState({});
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [declineBusy, setDeclineBusy] = useState(false);

  const loadDeclineCands = () =>
    api.get("/admin/broker/pilots/decline/candidates").then((r) => setDeclineCands(r.data)).catch(() => setDeclineCands([]));

  useEffect(() => {
    if (ready && (!user || user.role !== "admin")) nav("/");
  }, [ready, user, nav]);

  useEffect(() => {
    if (user && user.role === "admin") {
      Promise.all([
        api.get("/admin/broker/pilots").then((r) => setRows(r.data)).catch(() => setRows([])),
        api.get("/admin/waitlist").then((r) => setWaitlist(r.data)).catch(() => setWaitlist([])),
        loadDeclineCands(),
      ]).finally(() => setLoading(false));
    }
  }, [user]);

  const launch = async (capability) => {
    setBusy("wl" + capability);
    try {
      const { data } = await api.post("/admin/waitlist/launch", { capability });
      setWaitlist((ws) => ws.map((w) => (w.capability === capability ? { ...w, pending: data.sent ? w.pending - data.sent : w.pending } : w)));
      if (data.sent > 0) toast.success(t("admin.wl_launched").replace("{sent}", data.sent));
      else toast.warning(t("admin.wl_logs"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("common.error"));
    } finally { setBusy(null); setConfirmWl(null); setConfirmText(""); }
  };

  const confirmMatch = confirmWl && confirmText.trim().toLowerCase() === (confirmWl.name || "").toLowerCase();

  const selectedDeclineIds = declineCands.filter((c) => declineSel[c.id]).map((c) => c.id);

  const sendDeclines = async () => {
    setDeclineBusy(true);
    try {
      const { data } = await api.post("/admin/broker/pilots/decline/send", { pilot_ids: selectedDeclineIds });
      if (data.sent > 0) toast.success(t("admin.dec_sent").replace("{sent}", data.sent));
      else toast.warning(t("admin.dec_logs"));
      setDeclineSel({});
      await loadDeclineCands();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("common.error"));
    } finally { setDeclineBusy(false); setShowDeclineConfirm(false); }
  };

  const setStatus = async (id, status) => {
    setBusy(id + status);
    try {
      const { data } = await api.post(`/admin/broker/pilots/${id}/status`, { status });
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status: data.status } : r)));
      if (status === "approved") {
        if (data.email_notified) toast.success(t("admin.approved_emailed"));
        else if (data.email_error) toast.warning(t("admin.approved_email_failed"));
        else toast.success(t("admin.approved_logged"));
      } else {
        toast.success(t("admin.rejected_done"));
        loadDeclineCands();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("common.error"));
    } finally { setBusy(null); }
  };

  if (!ready || !user || user.role !== "admin" || loading)
    return <div className="p-16 text-white/50 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> {t("common.loading")}</div>;

  const badge = (s) => {
    const map = { approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", rejected: "bg-red-500/15 text-red-400 border-red-500/30", pending: "bg-gold/15 text-gold border-gold/25" };
    const label = { approved: t("admin.st_approved"), rejected: t("admin.st_rejected"), pending: t("admin.st_pending") }[s] || s;
    return <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${map[s] || map.pending}`}>{label}</span>;
  };

  const cols = t("admin.cols");
  const q = query.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (q && !((r.agency || "").toLowerCase().includes(q) || (r.areas || "").toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q))) return false;
    return true;
  });

  return (
    <div className="max-w-[1200px] mx-auto px-5 lg:px-8 py-14" data-testid="admin-pilots-page">
      <Eyebrow>{t("admin.eyebrow")}</Eyebrow>
      <h1 className="font-display text-4xl text-white mt-1">{t("admin.title")}</h1>
      <p className="text-white/55 mt-2">{t("admin.sub")}</p>

      {/* Waitlist launches */}
      <section className="mt-8" data-testid="admin-waitlist">
        <h2 className="font-display text-2xl text-white">{t("admin.wl_h")}</h2>
        <p className="text-white/55 text-sm mt-1">{t("admin.wl_sub")}</p>
        {waitlist.length === 0 ? (
          <div className="glass rounded-2xl p-6 mt-4 text-white/50 text-sm" data-testid="admin-waitlist-empty">{t("admin.wl_empty")}</div>
        ) : (
          <div className="glass rounded-2xl overflow-hidden mt-4">
            <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[11px] uppercase tracking-wider text-white/40 border-b border-white/10">
              <div className="col-span-5">{t("admin.wl_cap")}</div><div className="col-span-3">{t("admin.wl_total")}</div>
              <div className="col-span-2">{t("admin.wl_pending")}</div><div className="col-span-2 text-right">{t("admin.wl_action")}</div>
            </div>
            {waitlist.map((w) => (
              <div key={w.capability} data-testid="admin-waitlist-row" className="grid grid-cols-2 md:grid-cols-12 gap-2 items-center px-5 py-4 border-b border-white/5">
                <div className="md:col-span-5 text-white text-sm">{w.name} <span className="text-white/40 text-xs">({w.capability})</span></div>
                <div className="md:col-span-3 text-white/70 text-sm">{w.total}</div>
                <div className="md:col-span-2 text-gold text-sm">{w.pending}</div>
                <div className="md:col-span-2 flex md:justify-end items-center gap-2">
                  {!w.live && <span className="text-[10px] uppercase tracking-wider text-white/40">{t("admin.wl_locked")}</span>}
                  <button disabled={busy || w.pending === 0 || !w.live} onClick={() => { setConfirmWl(w); setConfirmText(""); }} data-testid="admin-waitlist-launch"
                    className="inline-flex items-center gap-1 text-xs bg-gold/15 text-gold border border-gold/25 rounded-lg px-3 py-1.5 hover:bg-gold/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200">
                    <Rocket size={13} /> {t("admin.wl_action")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Pilot decline notifications — explicit, reviewed, human-triggered */}
      <section className="mt-10" data-testid="admin-decline">
        <h2 className="font-display text-2xl text-white">{t("admin.dec_h")}</h2>
        <p className="text-white/55 text-sm mt-1">{t("admin.dec_sub")}</p>
        {declineCands.length === 0 ? (
          <div className="glass rounded-2xl p-6 mt-4 text-white/50 text-sm" data-testid="admin-decline-empty">{t("admin.dec_empty")}</div>
        ) : (
          <div className="glass rounded-2xl overflow-hidden mt-4">
            {declineCands.map((c) => (
              <label key={c.id} data-testid="admin-decline-row" className="flex items-center gap-3 px-5 py-3.5 border-b border-white/5 cursor-pointer hover:bg-white/[0.02] transition-colors duration-200">
                <input type="checkbox" checked={!!declineSel[c.id]} data-testid={`admin-decline-check-${c.id}`}
                  onChange={(e) => setDeclineSel((s) => ({ ...s, [c.id]: e.target.checked }))}
                  className="w-4 h-4 accent-gold" />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm">{c.name} <span className="text-white/40 text-xs">· {c.agency || "—"}</span></div>
                  <div className="text-white/40 text-xs flex items-center gap-1.5"><Mail size={11} /> {c.email}</div>
                </div>
              </label>
            ))}
            <div className="flex items-center justify-between px-5 py-3.5">
              <span className="text-xs text-white/50">{selectedDeclineIds.length} {t("admin.dec_selected")}</span>
              <button disabled={declineBusy || selectedDeclineIds.length === 0} data-testid="admin-decline-send"
                onClick={() => setShowDeclineConfirm(true)}
                className="inline-flex items-center gap-1.5 text-xs bg-white/5 text-white/70 border border-white/10 rounded-lg px-3 py-1.5 hover:text-gold hover:border-gold/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200">
                <Send size={13} /> {t("admin.dec_send")}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mt-10 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="admin-search"
            placeholder={t("admin.filter_search_ph")} className="bg-input/50 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold/40 w-64" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="admin-status-filter"
          className="bg-input/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-gold/40">
          <option value="all" className="bg-surface">{t("admin.filter_all")}</option>
          <option value="pending" className="bg-surface">{t("admin.st_pending")}</option>
          <option value="approved" className="bg-surface">{t("admin.st_approved")}</option>
          <option value="rejected" className="bg-surface">{t("admin.st_rejected")}</option>
        </select>
        <span className="text-xs text-white/40">{filtered.length}/{rows.length}</span>
      </div>

      {rows.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-white/50" data-testid="admin-pilots-empty">{t("admin.empty")}</div>
      ) : filtered.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-white/50" data-testid="admin-pilots-nomatch">{t("admin.no_match")}</div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden" data-testid="admin-pilots-list">
          <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[11px] uppercase tracking-wider text-white/40 border-b border-white/10">
            <div className="col-span-3">{cols[0]}</div><div className="col-span-2">{cols[1]}</div>
            <div className="col-span-3">{cols[2]}</div><div className="col-span-1">{cols[3]}</div>
            <div className="col-span-1">{cols[4]}</div><div className="col-span-2 text-right">{cols[5]}</div>
          </div>
          {filtered.map((r) => (
            <div key={r.id} data-testid="admin-pilot-row" className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center px-5 py-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors duration-200">
              <div className="md:col-span-3">
                <div className="text-white text-sm">{r.name}</div>
                <div className="text-white/40 text-xs">{new Date(r.created_at).toLocaleDateString()} · {r.areas || "—"}</div>
              </div>
              <div className="md:col-span-2 text-sm text-white/70">{r.agency}<div className="text-white/40 text-xs">{r.brn || "—"}</div></div>
              <div className="md:col-span-3 text-xs text-white/60">
                <div className="flex items-center gap-1.5"><Mail size={11} /> {r.email}</div>
                <div className="flex items-center gap-1.5 mt-0.5"><Phone size={11} /> {r.phone}</div>
              </div>
              <div className="md:col-span-1 text-sm text-white/70">{r.listings_per_month || "—"}</div>
              <div className="md:col-span-1">{badge(r.status)}</div>
              <div className="md:col-span-2 flex md:justify-end gap-1.5">
                <button disabled={busy || r.status === "approved"} onClick={() => setStatus(r.id, "approved")} data-testid="admin-approve"
                  className="inline-flex items-center gap-1 text-xs bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-lg px-2.5 py-1.5 hover:bg-emerald-500/25 disabled:opacity-40 transition-colors duration-200"><Check size={13} /> {t("admin.approve")}</button>
                <button disabled={busy || r.status === "rejected"} onClick={() => setStatus(r.id, "rejected")} data-testid="admin-reject"
                  className="inline-flex items-center gap-1 text-xs bg-white/5 text-white/60 border border-white/10 rounded-lg px-2.5 py-1.5 hover:text-red-400 hover:border-red-500/30 disabled:opacity-40 transition-colors duration-200"><X size={13} /> {t("admin.reject")}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmWl && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="wl-confirm-modal">
          <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" onClick={() => setConfirmWl(null)} />
          <div className="relative glass rounded-2xl p-7 w-full max-w-md">
            <h3 className="font-display text-xl text-white">{t("admin.wl_confirm_title")}</h3>
            <p className="text-white/60 text-sm mt-2">
              {t("admin.wl_confirm_body").replace("{count}", confirmWl.pending).replace("{name}", confirmWl.name)}
            </p>
            <label className="block text-xs text-white/50 mt-4 mb-1">{t("admin.wl_confirm_type")} <span className="text-gold">{confirmWl.name}</span></label>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="wl-confirm-input" autoFocus
              className="w-full bg-input/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-gold/40" />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setConfirmWl(null)} data-testid="wl-confirm-cancel" className="px-4 py-2 rounded-lg border border-white/10 text-white/70 text-sm hover:border-white/30 transition-colors duration-200">{t("admin.wl_cancel")}</button>
              <button disabled={!confirmMatch || busy} onClick={() => launch(confirmWl.capability)} data-testid="wl-confirm-launch"
                className="px-4 py-2 rounded-lg bg-gold text-ink font-semibold text-sm hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200">
                {t("admin.wl_confirm_btn")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeclineConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="decline-confirm-modal">
          <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" onClick={() => setShowDeclineConfirm(false)} />
          <div className="relative glass rounded-2xl p-7 w-full max-w-md">
            <h3 className="font-display text-xl text-white">{t("admin.dec_confirm_title")}</h3>
            <p className="text-white/60 text-sm mt-2">
              {t("admin.dec_confirm_body").replace("{count}", selectedDeclineIds.length)}
            </p>
            <div className="mt-4 max-h-40 overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5">
              {declineCands.filter((c) => declineSel[c.id]).map((c) => (
                <div key={c.id} className="px-3 py-2 text-xs text-white/60 flex items-center gap-1.5"><Mail size={11} /> {c.email}</div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowDeclineConfirm(false)} data-testid="decline-confirm-cancel" className="px-4 py-2 rounded-lg border border-white/10 text-white/70 text-sm hover:border-white/30 transition-colors duration-200">{t("admin.wl_cancel")}</button>
              <button disabled={declineBusy || selectedDeclineIds.length === 0} onClick={sendDeclines} data-testid="decline-confirm-send"
                className="px-4 py-2 rounded-lg bg-gold text-ink font-semibold text-sm hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200">
                {declineBusy ? t("common.loading") : t("admin.dec_confirm_btn")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
